import { useEffect, useRef, useState } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Play, Volume2, VolumeX } from "lucide-react";
import { CLIP_SECONDS, TRANSITION_MS, nextIndex } from "../data/reelSchedule";
import { playCue } from "./reelAudio";

// Sound needs a tap on the page first. Hovering does not count as a tap, so
// until the viewer has tapped, a clip stays muted and the reel offers a
// "Tap for sound" button whose click is the gesture the browser needs.
function tapped() {
  return navigator.userActivation ? navigator.userActivation.hasBeenActive : true;
}

/**
 * ScreeningReel: the homepage showreel. It plays a set of clips one after
 * another, each for a few seconds, with a fade in from black and a light sweep
 * on every cut, and a synthesized whoosh once the viewer has tapped. Hover
 * plays the current clip with sound; leaving mutes it again. Off screen the
 * reel holds still. Reduced motion turns off the automatic cuts.
 */
export default function ScreeningReel({ items, testid = "showreel-video" }) {
  const prm = useReducedMotion();
  const count = items.length;
  const [index, setIndex] = useState(0);
  const [visible, setVisible] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [audible, setAudible] = useState(false);
  const [needsTap, setNeedsTap] = useState(false);
  const wrapRef = useRef(null);
  const vidRef = useRef(null);
  const clip = items[index] ?? items[0];
  const isTouch = typeof window !== "undefined" && !!window.matchMedia?.("(hover: none)").matches;

  // Only run while on screen: no timers, no sound, no playback for a reel nobody can see.
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return undefined;
    }
    const io = new IntersectionObserver(
      ([entry]) => {
        setVisible(entry.isIntersecting);
        if (!entry.isIntersecting) {
          setAudible(false);
          setNeedsTap(false);
        }
      },
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // Play while visible; hold still otherwise and under reduced motion.
  useEffect(() => {
    const v = vidRef.current;
    if (!v) return;
    if (!visible || prm) {
      if (!v.paused) v.pause?.();
      return;
    }
    const started = v.play?.();
    if (started && typeof started.catch === "function") started.catch(() => {});
  }, [visible, prm, index]);

  // Each clip plays for CLIP_SECONDS, then the reel cuts to the next one.
  useEffect(() => {
    if (prm || !visible || count < 2) return undefined;
    const timer = setTimeout(() => {
      setIndex((i) => nextIndex(i, count));
      if (tapped()) playCue("whoosh");
    }, CLIP_SECONDS * 1000);
    return () => clearTimeout(timer);
  }, [prm, visible, index, count]);

  const onEnter = () => {
    if (prm || isTouch) return;
    if (tapped()) setAudible(true);
    else setNeedsTap(true);
  };

  const onLeave = () => {
    if (isTouch) return;
    setAudible(false);
    setNeedsTap(false);
  };

  const onTapForSound = (e) => {
    e.stopPropagation();
    setAudible(true);
    setNeedsTap(false);
    playCue("chime");
  };

  const jumpTo = (to) => {
    if (to === index) return;
    if (!prm && tapped()) playCue("whoosh");
    setIndex(to);
  };

  // Touch: a tap toggles sound. Reduced motion: a tap plays the clip with sound.
  const onStageClick = () => {
    if (prm) {
      setAudible(true);
      vidRef.current?.play?.()?.catch?.(() => {});
      return;
    }
    if (isTouch) {
      if (audible) {
        setAudible(false);
      } else {
        setAudible(true);
        playCue("chime");
      }
    }
  };

  if (!clip) return null;

  return (
    <div
      ref={wrapRef}
      data-testid={testid}
      data-index={index}
      data-count={count}
      data-slug={clip.slug}
      role="region"
      aria-roledescription="carousel"
      aria-label="Vyom showreel"
      className="group relative aspect-video w-full overflow-hidden bg-black"
      onMouseEnter={onEnter}
      onMouseLeave={onLeave}
      onClick={onStageClick}
    >
      <motion.div
        key={clip.slug}
        className="absolute inset-0"
        initial={prm ? false : { opacity: 0, scale: 1.035 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: TRANSITION_MS / 1000, ease: [0.2, 0.7, 0.2, 1] }}
      >
        <img
          src={clip.poster}
          alt=""
          aria-hidden="true"
          className="absolute inset-0 h-full w-full scale-110 object-cover opacity-50 blur-2xl"
        />
        <video
          ref={vidRef}
          src={clip.src}
          poster={clip.poster}
          muted={!audible}
          loop
          playsInline
          preload="auto"
          autoPlay={!prm && visible}
          onPlaying={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          className="absolute inset-0 h-full w-full object-contain"
        />
      </motion.div>

      {!prm && (
        <motion.div
          key={`sweep-${clip.slug}`}
          aria-hidden="true"
          className="pointer-events-none absolute inset-y-0 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent"
          initial={{ x: "-120%", opacity: 0 }}
          animate={{ x: "320%", opacity: [0, 0.9, 0] }}
          transition={{ duration: 1.1, ease: "easeInOut" }}
        />
      )}

      <span
        aria-hidden="true"
        className={`absolute right-3 top-3 grid h-8 w-8 place-items-center rounded-full border border-white/15 bg-black/55 text-white backdrop-blur transition-opacity duration-300 ${
          audible || needsTap ? "opacity-100" : "opacity-0 group-hover:opacity-100"
        }`}
      >
        {audible ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
      </span>

      {needsTap && !prm && (
        <button
          type="button"
          data-testid="reel-tap-for-sound"
          onClick={onTapForSound}
          className="absolute left-3 top-3 z-10 inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-black/65 px-3 py-1.5 font-mono text-[11px] uppercase tracking-[0.14em] text-white backdrop-blur"
        >
          <Volume2 className="h-3.5 w-3.5" />
          Tap for sound
        </button>
      )}

      {prm && !playing && (
        <span aria-hidden="true" className="pointer-events-none absolute inset-0 grid place-items-center">
          <span className="grid h-16 w-16 place-items-center rounded-full border border-white/20 bg-black/55 text-white">
            <Play className="h-6 w-6 fill-white" />
          </span>
        </span>
      )}

      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-2/5 bg-gradient-to-t from-black/85 via-black/20 to-transparent" />

      <div className="pointer-events-none absolute inset-x-0 bottom-0 p-4 sm:p-6">
        <motion.div
          key={`caption-${clip.slug}`}
          initial={prm ? false : { opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.3 }}
        >
          <div className="flex items-center gap-2">
            <span className="rounded-md border border-white/10 bg-black/50 px-2 py-0.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[#c9c1ff]">
              {clip.tag}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/45">{clip.label}</span>
          </div>
          <div
            data-testid="reel-title"
            className="mt-2 font-display text-xl font-semibold text-white sm:text-2xl"
            style={{ textShadow: "0 1px 12px rgba(0,0,0,0.7)" }}
          >
            {clip.title}
          </div>
        </motion.div>

        <div className="pointer-events-auto mt-4 flex items-center gap-2">
          {items.map((item, i) => (
            <button
              key={item.slug}
              type="button"
              data-testid={`reel-dot-${i}`}
              aria-label={`Play clip ${i + 1}: ${item.title}`}
              aria-current={i === index ? "true" : undefined}
              onClick={(e) => {
                e.stopPropagation();
                jumpTo(i);
              }}
              className={`h-1.5 rounded-full transition-all duration-500 ${
                i === index ? "w-8 bg-white" : "w-1.5 bg-white/40 hover:bg-white/70"
              }`}
            />
          ))}
          <span className="ml-auto font-mono text-[10px] uppercase tracking-[0.2em] text-white/60">
            Hover to listen · {index + 1}/{count}
          </span>
        </div>
      </div>

      {!prm && visible && (
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-0.5 bg-white/15">
          <motion.div
            key={`bar-${index}`}
            className="h-full origin-left bg-[#8c7cff]"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: CLIP_SECONDS, ease: "linear" }}
          />
        </div>
      )}
    </div>
  );
}
